
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavigationItem {
  name: string;
  path: string;
}

interface DesktopNavigationProps {
  mainNavItems: NavigationItem[];
  serviceNavItems: NavigationItem[];
}

const DesktopNavigation = ({ mainNavItems, serviceNavItems }: DesktopNavigationProps) => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isServiceActive = () => {
    const servicePaths = ["/offerings", "/hypnotherapy", "/trainings", "/somatic-coaching"];
    return servicePaths.includes(location.pathname);
  };

  const handleNavClick = (path: string) => {
    // If clicking on home link while already on home page, scroll to top
    if (path === "/" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Common active class for all menu items
  const activeClass = "text-kaija-accent font-bold";
  
  // Custom trigger style that applies to all menu items for consistent hover effects
  const commonLinkStyle = "text-xs font-medium uppercase tracking-wider text-gray-800 hover:text-kaija-accent transition-colors px-2";
  
  return (
    <nav className="flex items-center space-x-3">
      {/* Main nav items */}
      {mainNavItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={cn(
            commonLinkStyle,
            isActive(item.path) ? activeClass : ""
          )}
          onClick={() => handleNavClick(item.path)}
        >
          {item.name}
        </Link>
      ))}
      
      {/* Services Dropdown Menu */}
      <div className="relative group">
        <button 
          className={cn(
            commonLinkStyle,
            "flex items-center gap-1",
            isServiceActive() ? activeClass : ""
          )}
        >
          Teenused
          <ChevronDown className="h-3 w-3 transition-transform duration-200 group-hover:rotate-180" />
        </button>
        
        <div className="absolute left-0 mt-2 w-60 origin-top-right z-10 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100">
          <div className="py-2">
            {serviceNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "block px-4 py-2",
                  "text-sm font-medium text-gray-800 hover:text-kaija-accent hover:bg-gray-50 transition-colors",
                  isActive(item.path) ? "text-kaija-accent bg-gray-50" : ""
                )}
                onClick={() => handleNavClick(item.path)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      {/* Kontakt as the last menu item */}
      <Link
        to="/contact"
        className={cn(
          commonLinkStyle,
          isActive("/contact") ? activeClass : ""
        )}
        onClick={() => handleNavClick("/contact")}
      >
        Kontakt
      </Link>
    </nav>
  );
};

export default DesktopNavigation;
